/**
 * The one place that decides whether an error is an *expected client-level
 * response* (validation, wrong credentials, forbidden, not-found — user-facing,
 * the caller's to handle and show) or a *genuine application fault* (5xx, a
 * network/no-response failure, or an unexpected JS throw — worth observability).
 *
 * Root cause this fixes: expected errors like a wrong-password login were
 * reaching Sentry. A mistyped password is not an exception. Both the axios
 * interceptor (its own `captureException`) and the Sentry `beforeSend` gate
 * (which also catches the SDK's *default* unhandled-error/rejection handlers)
 * import these predicates, so the taxonomy can never drift between them.
 */

/** Extract an HTTP status from an axios-shaped error, or null (network / non-HTTP / JS error). */
export function getHttpStatus(err: unknown): number | null {
  const e = err as { response?: { status?: unknown }; status?: unknown };
  const status = e?.response?.status ?? e?.status;
  return typeof status === "number" ? status : null;
}

/**
 * Expected client-level response (4xx): validation, 401 wrong credentials / expired
 * session, 403, 404, 409, 422 … These are user-facing and must NEVER be reported.
 */
export function isExpectedClientError(err: unknown): boolean {
  const status = getHttpStatus(err);
  return status !== null && status >= 400 && status < 500;
}

/**
 * A genuine fault worth centralized observability: a server error (5xx), or a
 * network/no-response failure or an unexpected JS throw (both statusless). The
 * inverse of {@link isExpectedClientError} — everything that is not an expected
 * 4xx is treated as a fault so real bugs are never silently dropped.
 */
export function isAppFault(err: unknown): boolean {
  return !isExpectedClientError(err);
}
